import type { ReactElement } from "react";
export type RailSection =
  | "home"
  | "today"
  | "ledger"
  | "accounts"
  | "analytics"
  | "rules"
  | "calendar"
  | "health"
  | "ideas";

/**
 * The sections, down the left edge.
 *
 * Desktop-first: the rail is collapsed to icons and opens on hover or focus,
 * which buys back roughly 200px of width for the board - the screen that
 * actually needs it, because a signal row carries a matchup, a rule, a price,
 * a stake and an action and still has to be readable at a glance.
 *
 * Each item keeps a live count where one exists, so the rail says what is
 * waiting without being opened.
 */
const SECTIONS: readonly {
  readonly id: RailSection;
  readonly label: string;
  readonly hint: string;
  readonly icon: ReactElement;
}[] = [
  {
    id: "home",
    label: "Home",
    hint: "Everything at once",
    icon: (
      <>
        <path d="M4 10.5 11 4.5l7 6" />
        <path d="M5.8 12v6.5h10.4V12" />
      </>
    ),
  },
  {
    id: "today",
    label: "Today",
    hint: "Bets to place",
    icon: (
      <>
        <path d="M4 13.5 9 8l3.5 3.5L18 5" />
        <path d="M13.5 5H18v4.5" />
      </>
    ),
  },
  {
    id: "ledger",
    label: "Ledger",
    hint: "Placed and settled",
    icon: (
      <>
        <rect x="4" y="4" width="14" height="15" rx="2" />
        <path d="M7.5 9h7M7.5 12.5h7M7.5 16h4" />
      </>
    ),
  },
  {
    id: "accounts",
    label: "Accounts",
    hint: "Funds and limits",
    icon: (
      <>
        <rect x="3.5" y="6" width="17" height="12" rx="2" />
        <path d="M3.5 10h17" />
        <path d="M7 14.5h3" />
      </>
    ),
  },
  {
    id: "analytics",
    label: "Analytics",
    hint: "Is the edge real",
    icon: (
      <>
        <path d="M4 19V9M9.5 19V5M15 19v-6M20.5 19v-9" />
      </>
    ),
  },
  {
    id: "rules",
    label: "Rules",
    hint: "Pass, hold or bin",
    icon: (
      <>
        <path d="M5 7.5 7 9.5l3.5-3.5" />
        <path d="M5 15l2 2 3.5-3.5" />
        <path d="M13 8h6M13 15.5h6" />
      </>
    ),
  },
  {
    id: "calendar",
    label: "Calendar",
    hint: "Games and bets ahead",
    icon: (
      <>
        <rect x="4" y="5.5" width="14" height="13" rx="2" />
        <path d="M4 9.5h14M8 3.5v4M14 3.5v4" />
      </>
    ),
  },
  {
    id: "health",
    label: "Health",
    hint: "Feeds and budget",
    icon: (
      <>
        <path d="M3.5 12h4l2.5-6 3.5 12 2.5-6h4.5" />
      </>
    ),
  },
  {
    id: "ideas",
    label: "Ideas",
    hint: "Log it, deal with it later",
    icon: (
      <>
        <path d="M11 3.5a5.5 5.5 0 0 0-3.2 10v2.5h6.4v-2.5A5.5 5.5 0 0 0 11 3.5Z" />
        <path d="M8.5 19h5" />
      </>
    ),
  },
];

export function SectionRail({
  route,
  counts,
  onNavigate,
}: {
  readonly route: RailSection;
  readonly counts?: Partial<Record<RailSection, number>>;
  readonly onNavigate: (next: RailSection) => void;
}): ReactElement {
  return (
    <nav className="rail" aria-label="Sections">
      <ul>
        {SECTIONS.map((section) => {
          const count = counts?.[section.id];
          const current = route === section.id;
          return (
            <li key={section.id}>
              <button
                type="button"
                className={current ? "rail-item current" : "rail-item"}
                aria-current={current ? "page" : undefined}
                onClick={(event) => {
                  onNavigate(section.id);
                  // The rail stays open while anything inside it has focus,
                  // which is right for keyboard use and wrong immediately
                  // after a mouse click - it would sit over the page the
                  // click just navigated to. Only a real pointer click
                  // releases focus; keyboard activation reports no
                  // coordinates and keeps it.
                  if (event.detail > 0) event.currentTarget.blur();
                }}
              >
                <span className="rail-icon" aria-hidden="true">
                  <svg viewBox="0 0 22 22" fill="none">
                    {section.icon}
                  </svg>
                  {count !== undefined && count > 0 ? (
                    <span className="rail-pip" />
                  ) : null}
                </span>
                <span className="rail-body">
                  <span className="rail-label">{section.label}</span>
                  <span className="rail-hint">{section.hint}</span>
                </span>
                {count !== undefined && count > 0 ? (
                  <span className="rail-count tabular">{count}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
